import axios from 'axios';
import {
  applySessionAuthHeader,
  clearClientSession,
  purgeLegacyAuthTokens,
  refreshAuthSession,
  rememberAuthTokens,
} from '../../../../utils/authSession';

const baseURL = process.env.REACT_APP_APIURL;

const instance = axios.create({
  baseURL,
  timeout: 500000,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

let isRefreshing = false;
let refreshWaitQueue = [];

const processRefreshQueue = (error, ok = false) => {
  refreshWaitQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(ok);
  });
  refreshWaitQueue = [];
};

instance.interceptors.request.use(function (config) {
  const isFormData = config.data instanceof FormData;
  const headers = applySessionAuthHeader({
    ...config.headers,
    'X-Requested-With': 'XMLHttpRequest',
  });
  purgeLegacyAuthTokens();
  if (isFormData) {
    delete headers['Content-Type'];
  }
  return {
    ...config,
    withCredentials: true,
    headers,
  };
});

instance.interceptors.response.use(
  (response) => {
    if (response?.data && typeof response.data === 'object') {
      response.data = rememberAuthTokens(response.data);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || '';

    if (
      status !== 401 ||
      originalRequest?._retry ||
      requestUrl.includes('/user/refresh-token') ||
      requestUrl.includes('/user/login')
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshWaitQueue.push({ resolve, reject });
      }).then(() => instance(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      await refreshAuthSession(baseURL);
      processRefreshQueue(null, true);
      return instance(originalRequest);
    } catch (refreshError) {
      processRefreshQueue(refreshError, false);
      clearClientSession();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

const responseBody = (response) => response.data;

const requests = {
  get: (url, body) => instance.get(url, body).then(responseBody),

  post: (url, body, headers) =>
    instance.post(url, body, headers).then(responseBody),

  put: (url, body) => instance.put(url, body).then(responseBody),
  patch: (url, body) => instance.patch(url, body).then(responseBody),
  delete: (url, body) => instance.delete(url).then(responseBody),
  baseURL: () => baseURL,
};

export default requests;
